import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  Clipboard,
  ExternalLink,
  LoaderCircle,
  MonitorUp,
  RefreshCw,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { statsSalesPayloadSchema } from '../../lib/schemas';
import { getSocket } from '../../lib/socket';
import { appPath } from '../../lib/navigation';
import type { StatsPayload, StatsRoom, StatsSalesPayload } from '../../types';
import { StatsSparkline } from '../stats/StatsSparkline';
import { formatDateTime, formatMaybe, formatNumber, formatSigned, formatTime, roomDelta, toLocalInput, toneFor } from '../stats/statsCore';
import {
  getGuestAccess,
  getStats,
  getStatsSales,
  resetSalesSince,
  resetStatsBaseline,
  resyncStats,
  setRoomStatsStart,
  startStatsFresh,
} from './statsApi';
import { ModulePage } from './ModulePage';

type StatsView = 'boxes' | 'table';
type StatsSort = 'attendance' | 'movers' | 'vip' | 'room';
type StatsColumns = 'auto' | '2' | '3' | '4';

const sortLabels: Record<StatsSort, string> = {
  attendance: 'Attendance, high to low',
  movers: 'Biggest movers first',
  vip: 'VIP rooms first',
  room: 'Room order',
};

export function StatsDashboard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<StatsView>('boxes');
  const [sort, setSort] = useState<StatsSort>(() => storedChoice('modiq_stats_sort', ['attendance', 'movers', 'vip', 'room'], 'attendance'));
  const [columns, setColumns] = useState<StatsColumns>(() => storedChoice('modiq_stats_cols', ['auto', '2', '3', '4'], 'auto'));
  const [operationStatus, setOperationStatus] = useState('');
  const [salesPickerOpen, setSalesPickerOpen] = useState(false);

  const stats = useQuery({
    queryKey: queryKeys.stats,
    queryFn: ({ signal }) => getStats(signal),
    refetchInterval: 8_000,
  });
  const sales = useQuery({
    queryKey: queryKeys.statsSales,
    queryFn: ({ signal }) => getStatsSales(signal),
    refetchInterval: 8_000,
  });

  useEffect(() => {
    window.localStorage.setItem('modiq_stats_sort', sort);
  }, [sort]);
  useEffect(() => {
    window.localStorage.setItem('modiq_stats_cols', columns);
  }, [columns]);
  useEffect(() => {
    const socket = getSocket();
    const statsDirty = () => { void queryClient.invalidateQueries({ queryKey: queryKeys.stats }); };
    const salesUpdate = (payload: StatsSalesPayload) => {
      const parsed = statsSalesPayloadSchema.safeParse(payload);
      if (parsed.success) queryClient.setQueryData(queryKeys.statsSales, parsed.data);
    };
    socket.on('stats_dirty', statsDirty);
    socket.on('sales_update', salesUpdate);
    return () => {
      socket.off('stats_dirty', statsDirty);
      socket.off('sales_update', salesUpdate);
    };
  }, [queryClient]);

  const resync = useMutation({
    mutationFn: resyncStats,
    onSuccess: async result => {
      const synced = result.rooms.filter(room => room.ok).length;
      const missed = result.rooms.filter(room => !room.ok && room.reason !== 'not live');
      setOperationStatus(missed.length
        ? `${synced} synced. ${missed.map(room => `${room.room}: ${room.reason || 'unavailable'}`).join('; ')}`
        : (synced ? `${synced} room${synced === 1 ? '' : 's'} synced.` : 'No live rooms were available to sync.'));
      await queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
    onError: error => setOperationStatus(errorMessage(error, 'Could not resync attendance.')),
  });
  const baseline = useMutation({
    mutationFn: resetStatsBaseline,
    onSuccess: async () => {
      setOperationStatus('Attendance baseline reset.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
    onError: error => setOperationStatus(errorMessage(error, 'Could not reset the baseline.')),
  });
  const fresh = useMutation({
    mutationFn: startStatsFresh,
    onSuccess: async result => {
      const missed = result.rooms.filter(room => room.count == null && room.reason && room.reason !== 'not live');
      setOperationStatus(missed.length
        ? `Reset complete. Counts unavailable for ${missed.map(room => room.room).join(', ')}.`
        : 'Attendance restarted from current room counts.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
    onError: error => setOperationStatus(errorMessage(error, 'Could not restart attendance.')),
  });
  const copyTeamLink = useMutation({
    mutationFn: getGuestAccess,
    onSuccess: async access => {
      if (!access.passcodeSet) {
        setOperationStatus('Set a remote guest passcode in Zoom Setup before copying the team link.');
        return;
      }
      const url = `${window.location.origin}${appPath('/guest')}?g=${encodeURIComponent(access.token)}&to=stats`;
      try {
        await navigator.clipboard.writeText(url);
        setOperationStatus('Read-only team link copied.');
      } catch {
        window.prompt('Copy this team link:', url);
        setOperationStatus('Team link is ready to copy.');
      }
    },
    onError: error => setOperationStatus(errorMessage(error, 'Team link is not available.')),
  });

  const startFresh = () => {
    if (!isAdmin || fresh.isPending) return;
    const approved = window.confirm(
      'Start fresh?\n\nThis clears stored attendance history, peaks, and baselines for every live room, then restarts from the current room counts. This cannot be undone.',
    );
    if (approved) fresh.mutate();
  };

  const rooms = useMemo(() => sortRooms(stats.data?.rooms ?? [], sort), [sort, stats.data?.rooms]);
  const data = stats.data;
  return (
    <ModulePage
      id="stats-title"
      title="Stats"
      className="stats-dashboard"
      bodyClassName="stats-dashboard__body"
      meta={data ? <span className="stats-room-count">{data.demo ? 'Demo | ' : ''}{data.rooms.length} room{data.rooms.length === 1 ? '' : 's'}</span> : null}
      actions={(
        <div className="stats-header__toolbar">
          <SegmentedControl label="Attendance view" options={[[ 'boxes', 'Boxes' ], [ 'table', 'Table' ]]} value={view} onChange={value => setView(value as StatsView)} />
          {view === 'boxes' ? <SegmentedControl label="Tiles per row" options={[[ 'auto', 'Auto' ], [ '2', '2' ], [ '3', '3' ], [ '4', '4' ]]} value={columns} onChange={value => setColumns(value as StatsColumns)} /> : null}
          <button className="icon-button stats-tool" type="button" title="Open stats window" aria-label="Open stats window" onClick={() => window.open(appPath('/stats/window'), 'modiq_stats', 'popup=yes,width=760,height=520')}><ExternalLink size={16} /></button>
          <button className="icon-button stats-tool" type="button" title="Copy read-only team link" aria-label="Copy read-only team link" disabled={copyTeamLink.isPending} onClick={() => copyTeamLink.mutate()}>{copyTeamLink.isPending ? <LoaderCircle className="spin" size={16} /> : <Clipboard size={16} />}</button>
          <button className="icon-button stats-tool" type="button" title="Resync from Zoom" aria-label="Resync from Zoom" disabled={resync.isPending} onClick={() => resync.mutate()}>{resync.isPending ? <LoaderCircle className="spin" size={16} /> : <RefreshCw size={16} />}</button>
          <button className="secondary-button stats-tv" type="button" onClick={() => openStatsTv(columns)}><MonitorUp size={16} />TV</button>
        </div>
      )}
    >
      <div className="stats-dashboard__inner">
        <main className="stats-main">
          {operationStatus ? <div className="stats-operation" role="status"><span>{operationStatus}</span><button className="icon-button" type="button" title="Dismiss status" aria-label="Dismiss status" onClick={() => setOperationStatus('')}><X size={14} /></button></div> : null}

          {stats.isPending ? <div className="native-module__state"><LoaderCircle className="spin" size={20} />Loading attendance</div> : null}
          {stats.isError ? (
            <div className="native-module__state native-module__state--error" role="alert">
              <AlertTriangle size={22} /><strong>Attendance could not be loaded</strong>
              <span>{errorMessage(stats.error, 'Try refreshing attendance.')}</span>
              <button className="secondary-button" type="button" onClick={() => void stats.refetch()}>Try again</button>
            </div>
          ) : null}

          {data && data.rooms.length ? (
            <>
              <StatsSummary data={data} />
              {sales.data?.configured && sales.data.products.length ? (
                <SalesStrip
                  isAdmin={isAdmin}
                  sales={sales.data}
                  pickerOpen={salesPickerOpen}
                  setPickerOpen={setSalesPickerOpen}
                  onStatus={setOperationStatus}
                />
              ) : null}
              {view === 'boxes' ? <StatsBoxes columns={columns} rooms={rooms} total={data.totals.inRoomsNow} /> : <StatsTable data={data} rooms={rooms} />}
            </>
          ) : null}

          {data && data.rooms.length === 0 ? (
            <div className="native-module__state stats-empty">
              <BarChart3 size={24} /><strong>No live rooms yet</strong>
              <span>Attendance appears when a room is live and observable.</span>
            </div>
          ) : null}
        </main>

        <StatsSidebar
          data={data}
          isAdmin={isAdmin}
          sort={sort}
          setSort={setSort}
          resetting={baseline.isPending}
          startingFresh={fresh.isPending}
          onReset={() => baseline.mutate()}
          onFresh={startFresh}
        />
      </div>
    </ModulePage>
  );
}

function SegmentedControl({ label, options, value, onChange }: { label: string; options: Array<[string, string]>; value: string; onChange: (value: string) => void }) {
  return (
    <div className="segmented-control" role="group" aria-label={label}>
      {options.map(([key, text]) => <button className={value === key ? 'active' : ''} type="button" key={key} aria-pressed={value === key} onClick={() => onChange(key)}>{text}</button>)}
    </div>
  );
}

function StatsSummary({ data }: { data: StatsPayload }) {
  const totals = data.totals;
  const mover = totals.biggestDrop && totals.biggestDrop.delta < 0 ? totals.biggestDrop : totals.biggestGain;
  return (
    <div className="stats-summary" aria-label="Attendance totals">
      <StatsMetric label={data.demo ? 'Latest sample' : 'In rooms now'} value={formatNumber(totals.inRoomsNow)} meta={`${data.rooms.length} room${data.rooms.length === 1 ? '' : 's'}`} />
      <StatsMetric label={`Since ${formatTime(totals.hourStart) || '-'}`} value={formatSigned(totals.deltaTotal)} meta={`from ${formatNumber(totals.baseTotal)}`} tone={toneFor(totals.deltaTotal)} />
      <StatsMetric label={data.demo ? 'Peak sample' : 'Peak today'} value={formatNumber(totals.peak)} meta={totals.peakTs ? `at ${formatTime(totals.peakTs)}` : '-'} />
      <StatsMetric label={mover ? (mover.delta < 0 ? 'Biggest drop' : 'Biggest gain') : 'Movement'} value={mover ? formatSigned(mover.delta) : '-'} meta={mover?.name || 'No change yet'} tone={toneFor(mover?.delta ?? 0)} />
    </div>
  );
}

function StatsMetric({ label, value, meta, tone = '' }: { label: string; value: string; meta: string; tone?: string }) {
  return <div className={`stats-metric ${tone}`}><span>{label}</span><strong>{value}</strong><small>{meta}</small></div>;
}

function SalesStrip({
  isAdmin,
  sales,
  pickerOpen,
  setPickerOpen,
  onStatus,
}: {
  isAdmin: boolean;
  sales: StatsSalesPayload;
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  onStatus: (status: string) => void;
}) {
  const queryClient = useQueryClient();
  const reset = useMutation({
    mutationFn: (since?: number) => resetSalesSince(since),
    onSuccess: async result => {
      setPickerOpen(false);
      onStatus(`Sales now count from ${formatDateTime(result.sinceAt)}.`);
      await queryClient.invalidateQueries({ queryKey: queryKeys.statsSales });
    },
    onError: error => onStatus(errorMessage(error, 'Could not change the sales start time.')),
  });
  const since = sales.sinceAt ? `since ${formatDateTime(sales.sinceAt)}` : 'current window';
  return (
    <section className="stats-sales" aria-label="Sales since selected time">
      <SalesMetric label="All sales" value={sales.totalDelta} since={since} emphasized stale={Boolean(sales.error)}>
        {isAdmin ? (
          <div className="stats-sales__actions">
            <button type="button" disabled={reset.isPending} onClick={() => reset.mutate(undefined)}>Reset now</button>
            <button type="button" disabled={reset.isPending} onClick={() => setPickerOpen(true)}>Set time</button>
          </div>
        ) : null}
      </SalesMetric>
      {sales.products.map(product => <SalesMetric key={product.id} label={product.label} value={product.delta} since={since} />)}
      {pickerOpen ? <SalesTimeDialog initial={sales.sinceAt || sales.at} pending={reset.isPending} onCancel={() => setPickerOpen(false)} onApply={value => reset.mutate(value)} /> : null}
    </section>
  );
}

function SalesMetric({ label, value, since, emphasized = false, stale = false, children }: { label: string; value: number; since: string; emphasized?: boolean; stale?: boolean; children?: React.ReactNode }) {
  return (
    <div className={`stats-sale ${emphasized ? 'emphasized' : ''}`}>
      <span>{label}</span><strong className={toneFor(value)}>{formatSigned(value, false)}</strong>
      <small>{since}{stale ? ' | stale' : ''}</small>{children}
    </div>
  );
}

function SalesTimeDialog({ initial, pending, onCancel, onApply }: { initial: number | null; pending: boolean; onCancel: () => void; onApply: (value: number) => void }) {
  const [openedAt] = useState(() => Date.now());
  const [value, setValue] = useState(() => toLocalInput(initial || openedAt));
  return (
    <div className="sales-time-backdrop" role="presentation">
      <div className="sales-time-dialog" role="dialog" aria-modal="true" aria-labelledby="sales-time-title">
        <h3 id="sales-time-title">Count sales from</h3>
        <input type="datetime-local" aria-label="Sales start time" max={toLocalInput(openedAt)} value={value} onChange={event => setValue(event.target.value)} />
        <div><button className="secondary-button" type="button" disabled={pending} onClick={onCancel}>Cancel</button><button className="primary-button compact" type="button" disabled={pending || !value} onClick={() => onApply(new Date(value).getTime())}>Apply</button></div>
      </div>
    </div>
  );
}

function StatsBoxes({ rooms, total, columns }: { rooms: StatsRoom[]; total: number; columns: StatsColumns }) {
  return (
    <div className="stats-boxes" style={columns === 'auto' ? undefined : { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {rooms.map(room => <StatsRoomCard key={room.id} room={room} share={total > 0 && room.count != null ? room.count / total : 0} />)}
    </div>
  );
}

function StatsRoomCard({ room, share }: { room: StatsRoom; share: number }) {
  const delta = roomDelta(room);
  return (
    <article className={`stats-room-card ${toneFor(room.delta ?? 0)} ${room.ended ? 'ended' : ''}`}>
      <header><span className="stats-room-dot" style={{ background: room.count == null ? 'var(--idle)' : 'var(--signal)' }} /><h2>{room.name}</h2>{room.is_vip ? <span className="stats-room-chip vip">VIP</span> : room.type ? <span className="stats-room-chip">{room.type}</span> : null}</header>
      <div className="stats-room-card__numbers"><strong>{formatMaybe(room.count)}</strong><span className={delta.tone}>{delta.text}</span></div>
      <footer><span>{room.baseline == null ? 'No baseline' : `from ${formatNumber(room.baseline)}`}</span><StatsSparkline samples={room.samples} /></footer>
      <div className="stats-room-share" aria-label={`${Math.round(share * 100)} percent of current attendance`}><span style={{ width: `${Math.min(100, share * 100)}%` }} /></div>
    </article>
  );
}

function StatsTable({ data, rooms }: { data: StatsPayload; rooms: StatsRoom[] }) {
  return (
    <div className="stats-table-wrap">
      <table>
        <caption>Room attendance details</caption>
        <thead><tr><th scope="col">Room</th><th scope="col">Baseline</th><th scope="col">Now</th><th scope="col">Change</th><th scope="col">Last 30 min</th></tr></thead>
        <tbody>
          {rooms.map(room => {
            const delta = roomDelta(room);
            return <tr key={room.id}><th scope="row"><span className="stats-room-dot" style={{ background: room.count == null ? 'var(--idle)' : 'var(--signal)' }} />{room.name}{room.is_vip ? <span className="stats-room-chip vip">VIP</span> : null}</th><td>{formatMaybe(room.baseline)}</td><td>{formatMaybe(room.count)}</td><td className={delta.tone}>{delta.text}</td><td><StatsSparkline samples={room.samples} className="wide" /></td></tr>;
          })}
          <tr className="stats-table-total"><th scope="row">All rooms</th><td>{formatNumber(data.totals.baseTotal)}</td><td>{formatNumber(data.totals.inRoomsNow)}</td><td className={toneFor(data.totals.deltaTotal)}>{formatSigned(data.totals.deltaTotal)}</td><td /></tr>
        </tbody>
      </table>
    </div>
  );
}

function StatsSidebar({ data, isAdmin, sort, setSort, resetting, startingFresh, onReset, onFresh }: { data: StatsPayload | undefined; isAdmin: boolean; sort: StatsSort; setSort: (sort: StatsSort) => void; resetting: boolean; startingFresh: boolean; onReset: () => void; onFresh: () => void }) {
  const rooms = data?.rooms ?? [];
  const measurement = measurementStatus(rooms);
  const estimated = rooms.filter(room => room.countSource === 'estimate');
  const syncError = rooms.find(room => room.syncError)?.syncError;
  const vipNow = data?.totals.vipNow ?? 0;
  const total = data?.totals.inRoomsNow ?? 0;
  return (
    <aside className="stats-sidebar" aria-label="Attendance settings">
      <section>
        <h2><span>Measuring</span><span>{measurement.label}</span></h2>
        <div className="stats-progress"><span style={{ width: `${measurement.progress}%` }} /></div>
        <p>{measurement.description}</p>
        <button className="secondary-button stats-sidebar__button" type="button" disabled={!isAdmin || resetting || !rooms.length} title={isAdmin ? 'Reset attendance baseline' : 'Only an admin can reset the baseline'} onClick={onReset}>{resetting ? <LoaderCircle className="spin" size={14} /> : <RotateCcw size={14} />}Reset baseline</button>
        <button className="danger-button stats-sidebar__button" type="button" disabled={!isAdmin || startingFresh || !rooms.length} title={isAdmin ? 'Clear attendance history and restart' : 'Only an admin can restart attendance'} onClick={onFresh}>{startingFresh ? <LoaderCircle className="spin" size={14} /> : <Trash2 size={14} />}Start fresh</button>
        <small>Sampling every 60 seconds per room.</small>
      </section>
      {estimated.length ? <section className="stats-accuracy" role="status"><AlertTriangle size={16} /><p><strong>{estimated.length} room{estimated.length === 1 ? '' : 's'} showing an estimate.</strong> Resync from Zoom for an exact figure.{syncError ? ` ${syncError}` : ''}</p></section> : null}
      <section>
        <h2><span>Measure from</span><span>{isAdmin ? '' : 'Admin only'}</span></h2>
        {rooms.length ? rooms.map(room => <RoomStartControl key={`${room.id}:${room.statsStart ?? 'auto'}`} room={room} enabled={isAdmin && Boolean(data?.canEditStart)} />) : <p>No live rooms yet.</p>}
        <small>Times use your local timezone. Clear a value to return to the automatic baseline.</small>
      </section>
      <section>
        <label className="stats-sort"><span>Sort rooms by</span><select value={sort} onChange={event => setSort(event.target.value as StatsSort)}>{Object.entries(sortLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
      </section>
      <section>
        <h2><span>VIP rooms</span><span>{data?.totals.vipRooms ?? 0}</span></h2>
        <strong className="stats-vip-count">{formatNumber(vipNow)} <small>of {formatNumber(total)}</small></strong>
        <div className="stats-progress vip"><span style={{ width: `${total > 0 ? Math.min(100, (vipNow / total) * 100) : 0}%` }} /></div>
      </section>
    </aside>
  );
}

function RoomStartControl({ room, enabled }: { room: StatsRoom; enabled: boolean }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(() => toLocalInput(room.statsStart));
  const save = useMutation({
    mutationFn: (statsStart: number | null) => setRoomStatsStart(room.id, statsStart),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.stats }); },
  });
  const submit = (next: string) => {
    setValue(next);
    if (!enabled) return;
    save.mutate(next ? new Date(next).getTime() : null);
  };
  return (
    <div className="stats-room-start">
      <span title={room.name}><i style={{ background: room.color }} />{room.name}</span>
      <input type="datetime-local" aria-label={`${room.name} attendance start`} disabled={!enabled || save.isPending} value={value} onChange={event => submit(event.target.value)} />
      <button className="icon-button" type="button" title="Clear start time" aria-label={`Clear ${room.name} attendance start`} disabled={!enabled || save.isPending || !value} onClick={() => submit('')}><X size={13} /></button>
      {save.isError ? <small role="alert">{errorMessage(save.error, 'Could not save.')}</small> : null}
    </div>
  );
}

function measurementStatus(rooms: StatsRoom[]): { label: string; progress: number; description: string } {
  if (!rooms.length) return { label: '-', progress: 0, description: 'Attendance measurement begins when a room goes live.' };
  const settling = rooms.filter(room => room.settling || room.pending);
  if (settling.length) return { label: `${settling.length} settling`, progress: 40, description: 'Live counts are visible while the attendance-change baseline settles.' };
  const measuring = rooms.filter(room => room.count != null);
  const from = measuring.map(room => room.measureFrom).filter((value): value is number => value != null).sort((a, b) => a - b)[0];
  return { label: from ? `from ${formatTime(from)}` : 'Live', progress: measuring.length ? 100 : 0, description: `Measuring attendance change for ${measuring.length} observable room${measuring.length === 1 ? '' : 's'}.` };
}

function sortRooms(rooms: StatsRoom[], sort: StatsSort): StatsRoom[] {
  return [...rooms].sort((left, right) => {
    if (sort === 'movers') return Math.abs(right.delta ?? 0) - Math.abs(left.delta ?? 0);
    if (sort === 'vip') return Number(right.is_vip) - Number(left.is_vip) || (right.count ?? 0) - (left.count ?? 0);
    if (sort === 'room') return left.id - right.id;
    return (right.count ?? 0) - (left.count ?? 0);
  });
}

function storedChoice<T extends string>(key: string, choices: readonly T[], fallback: T): T {
  const saved = window.localStorage.getItem(key);
  return saved && choices.includes(saved as T) ? saved as T : fallback;
}

function openStatsTv(columns: StatsColumns) {
  const query = columns === 'auto' ? '' : `?cols=${columns}`;
  window.open(`${appPath('/stats/board')}${query}`, 'modiq_stats_tv', `popup=yes,width=${screen.availWidth},height=${screen.availHeight},left=0,top=0`);
}
